import React, {
  createContext,
  useCallback,
  useContext,
  type Dispatch,
  type SetStateAction,
  type PropsWithChildren,
  useState,
  useEffect,
} from 'react'
import type {
  Product,
  ProductPrice,
  ProductVariant,
  VariantCoupon,
  Options,
  FreeGift,
  BundleListItem,
  Bundle,
} from './types/product'
import type { CompareData, DiscountData } from './types.js'

type Country = {
  id?: number
  name?: string
  code?: string
}

export type UserProfile = {
  user_id: string
  email: string
  nick_name?: string
  full_name?: string
  first_name?: string
  last_name?: string
  dob?: string
  gender?: string
  phone_number?: string
  phone_account?: string
  country?: Country
  is_subscribe?: boolean
  avatar?: string
  can_change_password?: boolean
  invitation_code?: string
  phone_carrier?: string
  dob_programmed?: boolean
  activated?: boolean
  registed_at?: number
  activated_at?: number
  token?: string
}

export type SavingDetail = {
  coupon: number
  bundle: number
  member: number
  freeGift: number
  compare: number
  exchangePurchase: number
}

type variantWithFinalPrice = ProductVariant & { finalPrice?: ProductPrice }

export type FunctionMemberPriceResult = {
  hasMemberPrice: boolean
  originalPrice: number
  withCouponMemberTotalSave: number
  withoutCouponMaxMemberTotalSave?: number
  labels: {
    key: string
    value: string
    desc: string
  }[]
}

export type ModalData = {
  needClickAway?: boolean
  className?: string
  title?: string
  content?: string
  domContent?: React.ReactNode
}

export type JoinedRecommendBuyProducts = {
  bundle?: {
    value?: BundleListItem
    canOperate?: boolean
  }
  gift?: {
    value?: ProductVariant
    canOperate?: boolean
  }
  exchange?: {
    value?: ProductVariant
    canOperate?: boolean
  }
}

export type CreditsRedemption = {
  amount: number
  availablePoints: number
  usedPoints: number
  skuDetails?: {
    sku: string
    is_eligible: boolean
  }[]
}

export interface ProductContextType {
  isLogin?: boolean
  isMobile?: boolean
  isDesktop?: boolean
  product: Product
  profile?: UserProfile
  variant: ProductVariant
  selectedVariants: Array<variantWithFinalPrice>
  renderRating?: React.ReactNode
  coupon?: VariantCoupon
  savingDetail: SavingDetail
  setSavingDetail: Dispatch<SetStateAction<SavingDetail>>
  finalPrice: number
  comparePrice: number
  totalSavings: number
  selectedOptions: Options
  setSelectedOptions: Dispatch<SetStateAction<Options>>
  freeGift?: FreeGift
  checkedGift?: ProductVariant
  setCheckedGift?: Dispatch<SetStateAction<ProductVariant | undefined>>
  exchangePurchase?: FreeGift
  checkedExchangePurchase?: ProductVariant
  setCheckedExchangePurchase?: Dispatch<SetStateAction<ProductVariant | undefined>>
  checkedBundle?: BundleListItem
  setCheckedBundle: Dispatch<SetStateAction<BundleListItem | undefined>>
  joinedRecommendBuyProducts: JoinedRecommendBuyProducts
  setJoinedRecommendBuyProducts?: Dispatch<SetStateAction<JoinedRecommendBuyProducts>>
  checkedCareBundle?: ProductVariant
  setCheckedCareBundle?: Dispatch<SetStateAction<ProductVariant | undefined>>
  bundle?: Bundle
  compareData?: CompareData['data']
  onAddToCart?: () => void
  onBuyNow?: () => void
  creditsRedemption?: CreditsRedemption
  memberFunctionResult?: FunctionMemberPriceResult
  openModal: ({ title, content, domContent }: ModalData) => void
  openSignInPopup?: () => void
  openAuthCodePopup?: () => void
  openSignUpPopup?: () => void
  renderInstallments?: React.ReactNode
  onUseCouponChange?: (couponSaving: number) => void
  onUseMemberDiscountChange?: (memberSaving: number) => void
  discount?: DiscountData
  buyNowLoading?: boolean
  addToCartLoading?: boolean
  addOrder?: Array<'bundle' | 'gift' | 'exchange'>
  setAddOrder?: Dispatch<SetStateAction<Array<'bundle' | 'gift' | 'exchange'>>>
}

// const initialState: ProductContextType = {
//   isLogin: false,
//   isMobile: false,
//   product: {} as Product,
//   variant: {} as ProductVariant,
//   savingDetail: { coupon: 0, bundle: 0, member: 0, freeGift: 0, compare: 0 },
//   finalPrice: 0,
//   comparePrice: 0,
//   totalSavings: 0,
//   renderRating: null,
//   selectedOptions: {} as Options,
//   setSelectedOptions: () => {},
//   checkedBundle: undefined,
//   checkedGift: undefined,
//   setCheckedBundle: () => {},
//   setCheckedGift: () => {},
//   checkedCareBundle: undefined,
//   setCheckedCareBundle: () => {},
// }

export const BizProductContext = createContext<ProductContextType>({} as ProductContextType)

export const useBizProductContext = () => {
  const context = useContext(BizProductContext)
  if (!context) {
    throw new Error('useProductContext must be used in <ProductProvider>')
  }
  return useContext(BizProductContext)
}

const ProductProvider = ({
  product,
  isLogin,
  profile,
  isMobile,
  isDesktop,
  children,
  renderRating,
  freeGift,
  exchangePurchase,
  bundle,
  checkedBundle,
  checkedGift,
  setCheckedBundle,
  setCheckedGift,
  checkedExchangePurchase,
  setCheckedExchangePurchase,
  variant,
  selectedOptions,
  setSelectedOptions,
  compareData,
  coupon,
  finalPrice,
  comparePrice,
  totalSavings,
  savingDetail,
  setSavingDetail,
  selectedVariants,
  onAddToCart,
  onBuyNow,
  creditsRedemption,
  memberFunctionResult,
  openModal,
  openSignInPopup,
  openAuthCodePopup,
  openSignUpPopup,
  renderInstallments,
  onUseCouponChange,
  onUseMemberDiscountChange,
  discount,
  buyNowLoading,
  addToCartLoading,
}: PropsWithChildren<
  Omit<ProductContextType, 'joinedRecommendBuyProducts' | 'setJoinedRecommendBuyProducts' | 'addOrder' | 'setAddOrder'>
>) => {
  const [joinedRecommendBuyProducts, setJoinedRecommendBuyProducts] = useState<JoinedRecommendBuyProducts>({})
  const [addOrder, setAddOrder] = useState<Array<'bundle' | 'gift' | 'exchange'>>([])

  useEffect(() => {
    if (variant?.id) {
      setJoinedRecommendBuyProducts({
        bundle: {
          value: undefined,
          canOperate: true,
        },
        gift: {
          value: undefined,
          canOperate: true,
        },
        exchange: {
          value: undefined,
          canOperate: true,
        },
      })
      setAddOrder([])
    }
  }, [variant?.id])

  return (
    <BizProductContext.Provider
      value={{
        product,
        variant,
        isMobile,
        isDesktop,
        isLogin,
        profile,
        coupon,
        renderRating,
        finalPrice,
        comparePrice,
        savingDetail,
        setSavingDetail,
        totalSavings,
        selectedOptions,
        setSelectedOptions,
        freeGift,
        exchangePurchase,
        checkedGift,
        setCheckedGift,
        checkedBundle,
        setCheckedBundle,
        bundle,
        compareData,
        selectedVariants,
        onAddToCart,
        onBuyNow,
        joinedRecommendBuyProducts,
        setJoinedRecommendBuyProducts,
        checkedExchangePurchase,
        setCheckedExchangePurchase,
        creditsRedemption,
        memberFunctionResult,
        openModal,
        openSignInPopup,
        openAuthCodePopup,
        openSignUpPopup,
        renderInstallments,
        onUseCouponChange,
        onUseMemberDiscountChange,
        discount,
        buyNowLoading,
        addToCartLoading,
        addOrder,
        setAddOrder,
      }}
    >
      {children}
    </BizProductContext.Provider>
  )
}

export default ProductProvider
